import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { EditClientComponent } from './edit-client/edit-client.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ClientService, Client } from '../shared/services/client.service';
import { ApiService } from '../shared/services/api.service';

@Component({
  selector: 'app-client-list',
  templateUrl: './client-list.component.html',
  styleUrls: ['./client-list.component.css']
})
export class ClientListComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data properties
  clients: Client[] = [];
  filteredClients: Client[] = [];
  selectedClient: Client | null = null;
  
  // Material Table properties
  dataSource: MatTableDataSource<Client>;
  displayedColumns: string[] = [
    'client_name',
    // 'contact_person', 
    'contact_number',
    'location',
    'additional_info',
    'created_at',
    'actions'
  ];
  
  // Pagination properties
  totalClients = 0;
  pageSize = 10;
  currentPage = 0;
  
  // Loading states
  loading = false;
  loadingClient = false;
  
  // Search and filter
  searchTerm = '';
  selectedType = '';
  selectedState = '';
  
  // Dialog states
  showCreateDialog = false;
  showEditDialog = false;
  showViewDialog = false;
  showDeleteDialog = false;
  
  // Forms
  clientForm: FormGroup;
  
  // Client types for dropdown
  clientTypes = [
    { value: 'individual', label: 'Individual' },
    { value: 'corporate', label: 'Corporate' },
    { value: 'government', label: 'Government' }
  ];

  // Location data properties
  allState: any[] = [];
  allDistricts: any[] = [];
  allSubDistricts: any[] = [];
  allVillage: any[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private clientService: ClientService,
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private apiService: ApiService
  ) {
    this.initializeForm();
    this.dataSource = new MatTableDataSource<Client>([]);
  }

  ngOnInit(): void {
    this.loadClients();
    this.getstates();
    this.setupDataSource();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupDataSource(): void {
    // Custom filter predicate for searching across multiple fields
    this.dataSource.filterPredicate = (data: Client, filter: string) => {
      const searchStr = filter.toLowerCase();
      return (
        data.client_name?.toLowerCase().includes(searchStr) ||
        data.contact_person?.toLowerCase().includes(searchStr) ||
        data.contact_number?.toLowerCase().includes(searchStr) ||
        data.state?.toLowerCase().includes(searchStr) ||
        data.district?.toLowerCase().includes(searchStr) ||
        data.sub_district?.toLowerCase().includes(searchStr) ||
        data.villages?.toLowerCase().includes(searchStr) ||
        data.type?.toLowerCase().includes(searchStr)
      );
    };
  }

  private initializeForm(): void {
    this.clientForm = this.formBuilder.group({
      // client_name: ['', [Validators.required, Validators.minLength(2)]],
      client_first_name: ['', [Validators.required, Validators.minLength(2)]],
      client_last_name: ['', [Validators.required, Validators.minLength(2)]],
      // contact_person: ['', [Validators.required, Validators.minLength(2)]],
      contact_number: ['', [Validators.required, Validators.pattern(/^[\d\s\-\+\(\)]+$/)]],
      email: ['', Validators.required],
      line_1: ['', Validators.required],
      line_2: ['', Validators.required],
      state: ['', Validators.required],
      district: ['', Validators.required],
      sub_district: ['', Validators.required],
      villages: ['', Validators.required],
      khasra_no: [''],
      area: [''],
      type: ['individual', Validators.required]
    });

    // Add form value change listeners for dynamic location binding
    this.setupLocationChangeListeners();
  }

  // Material Table Methods
  applyFilter(): void {
    let filteredData = [...this.clients];

    // Apply search filter
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      filteredData = filteredData.filter(client =>
        client.client_name?.toLowerCase().includes(searchLower) ||
        client.contact_person?.toLowerCase().includes(searchLower) ||
        client.contact_number?.toLowerCase().includes(searchLower) ||
        client.state?.toLowerCase().includes(searchLower) ||
        client.district?.toLowerCase().includes(searchLower) ||
        client.sub_district?.toLowerCase().includes(searchLower) ||
        client.villages?.toLowerCase().includes(searchLower)
      );
    }

    // Apply type filter
    if (this.selectedType) {
      filteredData = filteredData.filter(client => client.type === this.selectedType);
    }

    // Apply state filter
    if (this.selectedState) {
      filteredData = filteredData.filter(client => client.state === this.selectedState);
    }

    this.dataSource.data = filteredData;
    this.totalClients = filteredData.length;
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
      this.paginator.firstPage();
    }
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applyFilter();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedType = '';
    this.selectedState = '';
    this.applyFilter();
  }

  /** Pagination range label (filtered list) */
  get paginationFrom(): number {
    if (!this.totalClients) return 0;
    return this.currentPage * this.pageSize + 1;
  }

  get paginationTo(): number {
    return Math.min((this.currentPage + 1) * this.pageSize, this.totalClients);
  }

  sortData(sort: Sort): void {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'client_name': return this.compare(a.client_name, b.client_name, isAsc);
        case 'contact_person': return this.compare(a.contact_person, b.contact_person, isAsc);
        case 'contact_number': return this.compare(a.contact_number, b.contact_number, isAsc);
        case 'location': return this.compare(a.state, b.state, isAsc);
        case 'created_at': return this.compare(new Date(a.created_at), new Date(b.created_at), isAsc);
        default: return 0;
      }
    });
  }

  private compare(a: any, b: any, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  // Existing methods continue here...
  private setupLocationChangeListeners(): void {
    // Listen to state changes
    this.clientForm.get('state')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (state) {
          this.getdistricts();
        } else {
          this.allDistricts = [];
          this.allSubDistricts = [];
          this.allVillage = [];
          this.clientForm.patchValue({
            district: '',
            sub_district: '',
            villages: ''
          });
        }
      });

    // Listen to district changes
    this.clientForm.get('district')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(district => {
        if (district) {
          this.getSubDistricts();
        } else {
          this.allSubDistricts = [];
          this.allVillage = [];
          this.clientForm.patchValue({
            sub_district: '',
            villages: ''
          });
        }
      });

    // Listen to sub-district changes
    this.clientForm.get('sub_district')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(subDistrict => {
        if (subDistrict) {
          this.getVillages();
        } else {
          this.allVillage = [];
          this.clientForm.patchValue({
            villages: ''
          });
        }
      });
  }

  loadClients(): void {
    this.loading = true;
    this.clientService.getClients()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.clients = response.data || [];
          this.dataSource.data = this.clients;
          this.totalClients = this.clients.length;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading clients:', error);
          this.snackBar.open('Failed to load clients', 'Close', { duration: 3000 });
          this.loading = false;
        }
      });
  }

  getstates(): void {
    this.apiService.getstates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.allState = response || [];
        },
        error: (error: any) => {
          console.error('Error loading states:', error);
        }
      });
  }

  getdistricts(): void {
    const state = this.clientForm.get('state')?.value;
    if (state) {
      this.apiService.getdistricts(state)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.allDistricts = response || [];
          },
          error: (error: any) => {
            console.error('Error loading districts:', error);
          }
        });
    }
  }

  getSubDistricts(): void {
    const state = this.clientForm.get('state')?.value;
    const district = this.clientForm.get('district')?.value;
    if (state && district) {
      this.apiService.getSubdistricts(state, district)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.allSubDistricts = response || [];
          },
          error: (error: any) => {
            console.error('Error loading sub-districts:', error);
          }
        });
    }
  }

  getVillages(): void {
    const state = this.clientForm.get('state')?.value;
    const district = this.clientForm.get('district')?.value;
    const subDistrict = this.clientForm.get('sub_district')?.value;
    if (state && district && subDistrict) {
      this.apiService.getvillages(state, district, subDistrict)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.allVillage = response || [];
          },
          error: (error: any) => {
            console.error('Error loading villages:', error);
          }
        });
    }
  }

  // Dialog methods
  openCreateDialog(): void {
    this.showCreateDialog = true;
    this.clientForm.reset({
      type: 'individual'
    });
  }

  closeCreateDialog(): void {
    this.showCreateDialog = false;
    this.clientForm.reset();
  }

  openEditDialog(client: Client): void {
    const dialogRef = this.dialog.open(EditClientComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: { client: client },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        this.loadClients();
        this.snackBar.open('Client updated successfully', 'Close', { duration: 3000 });
      }
    });
  }

  closeEditDialog(): void {
    this.showEditDialog = false;
    this.selectedClient = null;
  }

  openViewDialog(client: Client): void {
    this.selectedClient = client;
    this.showViewDialog = true;
  }

  closeViewDialog(): void {
    this.showViewDialog = false;
    this.selectedClient = null;
  }

  openDeleteDialog(client: Client): void {
    this.selectedClient = client;
    this.showDeleteDialog = true;
  }

  closeDeleteDialog(): void {
    this.showDeleteDialog = false;
    this.selectedClient = null;
  }

  // CRUD operations
  createClient(): void {
    if (this.clientForm.valid) {
      const formData = this.clientForm.value;
      formData.client_name = `${formData.client_first_name} ${formData.client_last_name}`;
      
      this.clientService.createClient(formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response?.status === 'error') {
              this.snackBar.open(response.message || 'Failed to create client', 'Close', { duration: 5000 });
              return;
            }
            this.snackBar.open('Client created successfully', 'Close', { duration: 3000 });
            this.closeCreateDialog();
            this.loadClients();
          },
          error: (error) => {
            console.error('Error creating client:', error);
            this.snackBar.open(
              this.clientService.getApiErrorMessage(error, 'Failed to create client'),
              'Close',
              { duration: 5000 }
            );
          }
        });
    }
  }

  updateClient(): void {
    if (this.clientForm.valid && this.selectedClient) {
      const formData = this.clientForm.value;
      formData.client_name = `${formData.client_first_name} ${formData.client_last_name}`;
      
      this.clientService.updateClient(this.selectedClient.client_id!, formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response?.status === 'error') {
              this.snackBar.open(response.message || 'Failed to update client', 'Close', { duration: 5000 });
              return;
            }
            this.snackBar.open('Client updated successfully', 'Close', { duration: 3000 });
            this.closeEditDialog();
            this.loadClients();
          },
          error: (error) => {
            console.error('Error updating client:', error);
            this.snackBar.open(
              this.clientService.getApiErrorMessage(error, 'Failed to update client'),
              'Close',
              { duration: 5000 }
            );
          }
        });
    }
  }

  deleteClient(): void {
    if (this.selectedClient) {
      this.clientService.deleteClient(this.selectedClient.client_id!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.snackBar.open('Client deleted successfully', 'Close', { duration: 3000 });
            this.closeDeleteDialog();
            this.loadClients();
          },
          error: (error) => {
            console.error('Error deleting client:', error);
            this.snackBar.open('Failed to delete client', 'Close', { duration: 3000 });
          }
        });
    }
  }

  // Utility methods
  getFieldError(fieldName: string): string {
    const field = this.clientForm.get(fieldName);
    if (field && field.errors) {
      if (field.errors['required']) return `${fieldName.replace('_', ' ')} is required`;
      if (field.errors['minlength']) return `${fieldName.replace('_', ' ')} must be at least ${field.errors['minlength'].requiredLength} characters`;
      if (field.errors['pattern']) return `Invalid ${fieldName.replace('_', ' ')} format`;
    }
    return '';
  }

  getTypeClass(type: string): string {
    switch (type) {
      case 'individual': return 'type-individual';
      case 'corporate': return 'type-corporate';
      case 'government': return 'type-government';
      default: return 'type-default';
    }
  }

  getTypeText(type: string): string {
    switch (type) {
      case 'individual': return 'Individual';
      case 'corporate': return 'Corporate';
      case 'government': return 'Government';
      default: return 'Unknown';
    }
  }

  formatPhoneNumber(phone: string): string {
    if (!phone) return '';
    // Remove all non-digits
    const cleaned = phone.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return phone;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
}

